import { useHistory, useParams } from "react-router-dom";
import { useDealQuery, useUpdateDealMutation } from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { Steps } from "./CreateDeal";
import Loading from "../../components/Loading";
import { useState, useEffect } from "react";
import { Card } from "../../components/Card";
import { Button } from "../../components/tailwind/Button";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "../../components/tailwind/Checkbox";

export function DataRoomIndexing() {
  const { dealId } = useParams<{ dealId: string }>();
  const history = useHistory();

  const queryClient = useQueryClient();
  const client = useGqlClient();

  const dealQuery = useDealQuery(
    client,
    {
      id: dealId,
    },
    {
      enabled: dealId !== "",
    },
  );

  const updateDealMutation = useUpdateDealMutation(client);

  const [indexingEnabled, setIndexingEnabled] = useState(false);

  useEffect(() => {
    if (dealQuery.data) {
      setIndexingEnabled(dealQuery.data.deal.indexing);
    }
  }, [dealQuery.data]);

  if (dealQuery.error) {
    return (
      <div>
        <p>Something went wrong</p>
      </div>
    );
  }

  if (dealQuery.isPending || !dealQuery.data) {
    return <Loading />;
  }

  const handleToggleIndexing = () => {
    setIndexingEnabled(!indexingEnabled);
  };

  return (
    <div className="flex-1">
      <div className="flex justify-center">
        <div className="hidden xl:block">
          <Steps currentStep={3} />
        </div>
        <div className="w-full xl:w-1/3">
          <p className="text-lg font-semibold">Document Indexing</p>
          <p className="text-sm text-gray-500 font-light">
            Choose whether you want to enable document indexing for your data
            room. Indexing allows you to navigate and reference documents and
            folders by index.
          </p>

          <div className="mt-6">
            <Card padding="m">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={indexingEnabled}
                  onChange={handleToggleIndexing}
                  id="enable-indexing"
                  className="mt-1"
                />
                <div>
                  <label
                    htmlFor="enable-indexing"
                    className="block text-sm font-medium text-gray-900"
                  >
                    Enable indexing for this dataroom
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    When enabled, documents and folders in this data room will
                    be indexed.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <div className="flex justify-end mt-8 gap-x-3">
            <Button
              onClick={() => {
                history.push(`/create-deal/${dealId}/data-room`);
              }}
              variant="neutral"
              text="Back"
            />
            <Button
              onClick={() => {
                updateDealMutation.mutate(
                  {
                    input: {
                      id: dealId,
                      indexing: indexingEnabled,
                    },
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({
                        queryKey: ["Deal", { id: dealId }],
                      });
                      history.push(`/deal/${dealId}/dashboard`);
                    },
                  },
                );
              }}
              isLoading={updateDealMutation.isPending}
              loadingText="Saving..."
              variant="positive"
              text="Continue"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
